/**
 * Pure state machine for shooter-turn recording ids. The state is persisted
 * inside ActiveGame (gameId/turnId/lastSeq) so a reload resumes the current
 * turn rather than splitting it. No side effects, no storage — callers persist.
 */
export interface RecordingState {
  gameId: string;
  turnId: number;
  lastSeq: number;
}

/** Start a fresh game at turn 1, seq 0 (only the keyframe exists yet). */
export function newGame(gameId: string): RecordingState {
  return { gameId, turnId: 1, lastSeq: 0 };
}

/** Assign the next event seq within the current turn. */
export function nextSeq(state: RecordingState): { state: RecordingState; seq: number } {
  const seq = state.lastSeq + 1;
  return { state: { ...state, lastSeq: seq }, seq };
}

/** Seven-out boundary: start the next turn (seq resets to 0). */
export function advanceTurn(state: RecordingState): RecordingState {
  return { ...state, turnId: state.turnId + 1, lastSeq: 0 };
}

/**
 * True only if a persisted active game carries a valid turn-model recording
 * identity to resume. A legacy/cross-deploy pointer (no gameId, or non-integer
 * turn counters) returns false, so the caller starts a fresh game instead of
 * resuming with undefined ids.
 */
export function canResumeRecording(
  active: { gameId?: unknown; turnId?: unknown; lastSeq?: unknown } | null | undefined,
): boolean {
  return (
    !!active &&
    typeof active.gameId === 'string' &&
    active.gameId.length > 0 &&
    Number.isInteger(active.turnId) &&
    Number.isInteger(active.lastSeq)
  );
}
